import { useEffect, useRef, useState } from "react";
import { TrendingUp, ShoppingBag, Clock, BarChart3 } from "lucide-react";
import { useTranslation } from "@/i18n";

const CaseStudy = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-b from-[#0f1117] to-[#1a1d2e]">
      {/* Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[rgba(79,60,255,0.08)] blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-sm font-medium mb-6">
            <TrendingUp size={16} className="text-primary" />
            <span className="text-white/80">{t('caseStudy.badge')}</span>
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('caseStudy.title')}
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            {t('caseStudy.subtitle')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Metrics */}
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-5 rounded-xl bg-white/[0.06] border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <ShoppingBag className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{t('caseStudy.productsOptimized')}</p>
                  <p className="text-3xl font-extrabold text-white">420</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 rounded-xl bg-white/[0.06] border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{t('caseStudy.totalTime')}</p>
                  <p className="text-3xl font-extrabold text-white">3h</p>
                  <p className="text-xs text-white/35">{t('caseStudy.vsManual')}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 rounded-xl bg-white/[0.06] border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-[hsl(142,71%,45%)]/15 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-[hsl(142,71%,45%)]" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{t('caseStudy.clicksLabel')}</p>
                  <p className="text-3xl font-extrabold text-white">0 → 2.4K</p>
                  <p className="text-xs text-white/35">{t('caseStudy.clicksPeriod')}</p>
                </div>
              </div>
            </div>

            {/* Right - SVG Graph */}
            <div className="p-6 rounded-2xl bg-white/[0.04] border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium text-white/60">{t('caseStudy.graphTitle')}</p>
                <span className="text-xs px-2 py-1 rounded-full bg-[hsl(142,71%,45%)]/15 text-[hsl(142,71%,45%)] font-medium">
                  {t('caseStudy.typicalResult')}
                </span>
              </div>
              
              <svg viewBox="0 0 400 200" className="w-full h-auto" role="img" aria-label="Search console growth chart">
                {/* Grid lines */}
                <line x1="40" y1="20" x2="40" y2="170" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                <line x1="40" y1="170" x2="380" y2="170" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                <line x1="40" y1="120" x2="380" y2="120" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4" />
                <line x1="40" y1="70" x2="380" y2="70" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4" />

                {/* Y axis labels */}
                <text x="35" y="175" textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize="10">0</text>
                <text x="35" y="125" textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize="10">1K</text>
                <text x="35" y="75" textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize="10">2K</text>

                {/* Gradient fill */}
                <defs>
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(142,71%,45%)" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="hsl(142,71%,45%)" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Area fill */}
                <path
                  d={visible
                    ? "M40,170 L100,168 L140,165 L180,160 L200,145 L230,110 L270,80 L310,55 L350,35 L380,25 L380,170 Z"
                    : "M40,170 L380,170 L380,170 Z"
                  }
                  fill="url(#lineGradient)"
                  className="transition-all duration-[2000ms] ease-out"
                />

                {/* Line */}
                <path
                  d={visible
                    ? "M40,170 L100,168 L140,165 L180,160 L200,145 L230,110 L270,80 L310,55 L350,35 L380,25"
                    : "M40,170 L380,170"
                  }
                  fill="none"
                  stroke="hsl(142,71%,45%)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="transition-all duration-[2000ms] ease-out"
                />

                {/* Rankaro activation marker */}
                <line x1="180" y1="20" x2="180" y2="170" stroke="rgba(79,70,229,0.5)" strokeWidth="1" strokeDasharray="4" />
                <rect x="152" y="8" width="56" height="16" rx="4" fill="rgba(79,70,229,0.3)" />
                <text x="180" y="19" textAnchor="middle" fill="hsl(243,75%,59%)" fontSize="8" fontWeight="600">Rankaro ↑</text>
              </svg>

              <p className="text-xs text-white/35 mt-3 text-center">{t('caseStudy.graphDisclaimer')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudy;
